// COMBINATION ANALYZER — transparent, honest, DOM-free (mobile-shareable).
//
// This is an ANALYTICAL tool, NOT bet placement and NOT a profit claim.
// It works ONLY on real fields already supplied per selection (market, confidence,
// edge, kickoff, league). It does NOT fabricate a combined probability by
// multiplying arbitrary confidence values. Deeper ATLAS evaluation of a combination
// requires authenticated backend intelligence and is surfaced as an unavailable
// state until the owner supplies the auth contract.

import { marketTypeRisk, RiskLevel } from "@/presentation/riskModel";

export interface AnalysisSelection {
  fixtureId: string;
  home: string;
  away: string;
  country: string | null;
  league: string | null;
  marketCode: string;
  marketName: string | null;
  confidence: number | null;   // model_probability
  edge: number | null;
  referenceOdd: number | null;
  kickoff: string | null;      // ISO
  addedAt: number;
}

export interface CombinationInsight {
  count: number;
  overallRisk: RiskLevel;               // qualitative, rule-based (NOT a probability)
  overallRiskBasis: string;             // transparent explanation of how it was derived
  strongest: AnalysisSelection | null;  // highest confidence (real field)
  weakest: AnalysisSelection | null;    // lowest confidence (real field)
  highVarianceLegs: AnalysisSelection[];// legs whose MARKET TYPE is high-variance
  marketConcentration: Array<{ key: string; count: number }>;
  leagueConcentration: Array<{ key: string; count: number }>;
  timingConflicts: Array<{ a: string; b: string; note: string }>; // overlapping kickoffs
  reviewCandidates: AnalysisSelection[]; // suggested removal candidates (transparent reasons)
  atlasDeepEvaluation: { available: boolean; reason: string };
}

const RISK_WEIGHT: Record<RiskLevel, number> = { LOW: 1, MEDIUM: 2, HIGH: 3 };

export function analyzeCombination(sels: AnalysisSelection[], lang: "tr" | "en" = "tr"): CombinationInsight {
  const count = sels.length;

  // market-type risk per leg (transparent classification of the bet type)
  const legRisk = sels.map((s) => ({ sel: s, level: marketTypeRisk(s.marketCode, lang).level as RiskLevel }));
  const highVarianceLegs = legRisk.filter((l) => l.level === "HIGH").map((l) => l.sel);

  // overall risk = mean of leg market-type weights, escalated by concentration
  const meanWeight = count ? legRisk.reduce((a, l) => a + RISK_WEIGHT[l.level], 0) / count : 1;

  const marketConcentration = tally(sels.map((s) => s.marketCode));
  const leagueConcentration = tally(sels.map((s) => s.league || "—"));
  const maxMarketShare = count ? Math.max(...marketConcentration.map((m) => m.count)) / count : 0;
  const maxLeagueShare = count ? Math.max(...leagueConcentration.map((m) => m.count)) / count : 0;
  const concentrated = maxMarketShare >= 0.6 || maxLeagueShare >= 0.6;

  let overallRisk: RiskLevel = meanWeight >= 2.4 ? "HIGH" : meanWeight >= 1.6 ? "MEDIUM" : "LOW";
  if (concentrated && overallRisk === "LOW") overallRisk = "MEDIUM";
  else if (concentrated && overallRisk === "MEDIUM") overallRisk = "HIGH";

  const overallRiskBasis = lang === "tr"
    ? `Her seçimin market türü riski ve yoğunlaşma (aynı market/lig) dikkate alınarak hesaplandı. Bu bir kazanç tahmini değildir.`
    : `Derived from each leg's market-type risk and concentration (same market/league). This is not a profit estimate.`;

  const withConf = sels.filter((s) => s.confidence != null);
  const strongest = withConf.length ? withConf.reduce((a, b) => (b.confidence! > a.confidence! ? b : a)) : null;
  const weakest = withConf.length ? withConf.reduce((a, b) => (b.confidence! < a.confidence! ? b : a)) : null;

  // timing conflicts: kickoffs within 15 minutes of each other
  const timingConflicts: Array<{ a: string; b: string; note: string }> = [];
  const dated = sels.filter((s) => s.kickoff).map((s) => ({ s, t: new Date(s.kickoff as string).getTime() })).filter((x) => !isNaN(x.t));
  for (let i = 0; i < dated.length; i++) {
    for (let j = i + 1; j < dated.length; j++) {
      if (Math.abs(dated[i].t - dated[j].t) <= 15 * 60000) {
        timingConflicts.push({
          a: `${dated[i].s.home} — ${dated[i].s.away}`,
          b: `${dated[j].s.home} — ${dated[j].s.away}`,
          note: lang === "tr" ? "Başlama saatleri çakışıyor" : "Kickoffs overlap",
        });
      }
    }
  }

  // review candidates: weakest confidence + high-variance legs (deduped)
  const reviewSet = new Map<string, AnalysisSelection>();
  if (weakest && (weakest.confidence == null || weakest.confidence < 55)) reviewSet.set(weakest.fixtureId + weakest.marketCode, weakest);
  highVarianceLegs.forEach((s) => reviewSet.set(s.fixtureId + s.marketCode, s));
  const reviewCandidates = Array.from(reviewSet.values());

  return {
    count,
    overallRisk,
    overallRiskBasis,
    strongest,
    weakest,
    highVarianceLegs,
    marketConcentration,
    leagueConcentration,
    timingConflicts,
    reviewCandidates,
    atlasDeepEvaluation: {
      available: false,
      reason: lang === "tr"
        ? "ATLAS'ın kombinasyon üzerinde derinlemesine değerlendirmesi, hesabın bağlandığında etkinleşecek."
        : "ATLAS deep evaluation of the combination activates once your account is connected.",
    },
  };
}

function tally(keys: string[]): Array<{ key: string; count: number }> {
  const m = new Map<string, number>();
  keys.forEach((k) => m.set(k, (m.get(k) || 0) + 1));
  return Array.from(m.entries()).map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count);
}
